/**
 * AdminMedicines.jsx  — Master Admin Only
 *
 * Features:
 *  • Excel-like TagGrid for dravyas & indications (Tab/Enter/Backspace/Paste)
 *  • Note / warning field per medicine
 *  • Excel bulk-upload: drag-drop or browse  (POST /api/medicines/bulk-upload)
 *  • Downloadable template Excel with correct column names
 */

import { useEffect, useState, useRef, useCallback } from "react";
import api from "../../services/api";

/* ─── Excel-like tag grid ─────────────────────────────────────────── */
function TagGrid({ values, onChange, placeholder, color = "blue" }) {
  const refs = useRef([]);
  const c = color === "green"
    ? { bg: "#F0FDF4", border: "#BBF7D0", text: "#166534", focus: "#22C55E" }
    : { bg: "#EFF6FF", border: "#BFDBFE", text: "#1E40AF", focus: "#3B82F6" };

  const update = (idx, val) => { const n = [...values]; n[idx] = val; onChange(n); };

  const addCell = (afterIdx) => {
    const n = [...values]; n.splice(afterIdx + 1, 0, ""); onChange(n);
    setTimeout(() => refs.current[afterIdx + 1]?.focus(), 30);
  };

  const removeCell = (idx) => {
    if (values.length <= 1) { onChange([""]); return; }
    onChange(values.filter((_, i) => i !== idx));
    setTimeout(() => refs.current[Math.max(0, idx - 1)]?.focus(), 30);
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Tab" || e.key === "Enter") {
      e.preventDefault();
      idx === values.length - 1 ? addCell(idx) : refs.current[idx + 1]?.focus();
    }
    if (e.key === "Backspace" && values[idx] === "") { e.preventDefault(); removeCell(idx); }
  };

  const handlePaste = (e, idx) => {
    e.preventDefault();
    const parts = e.clipboardData.getData("text")
      .split(/[\n\r\t,;|]+/).map(p => p.trim()).filter(Boolean);
    if (!parts.length) return;
    const n = [...values]; n.splice(idx, 1, ...parts);
    if (n[n.length - 1] !== "") n.push("");
    onChange(n);
    setTimeout(() => refs.current[idx + parts.length - 1]?.focus(), 30);
  };

  const cells = values.length === 0 ? [""] : values;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "8px 10px", background: "#FAFAFA", border: "1px solid #E5E7EB", borderRadius: 10, minHeight: 44 }}>
      {cells.map((val, idx) => (
        <div key={idx} style={{ display: "flex", alignItems: "center" }}>
          <input
            ref={el => refs.current[idx] = el}
            value={val}
            onChange={e => update(idx, e.target.value)}
            onKeyDown={e => handleKeyDown(e, idx)}
            onPaste={e => handlePaste(e, idx)}
            placeholder={idx === 0 ? placeholder : ""}
            style={{
              background: val ? c.bg : "#fff",
              border: `1px solid ${val ? c.border : "#E5E7EB"}`,
              borderRadius: 6, padding: "3px 8px", fontSize: 12,
              color: val ? c.text : "#9CA3AF", outline: "none",
              width: Math.max(80, (val.length || placeholder?.length || 8) * 7.5 + 20),
              minWidth: 80, maxWidth: 240,
              fontFamily: "'Inter', sans-serif",
            }}
            onFocus={e => e.target.style.borderColor = c.focus}
            onBlur={e => e.target.style.borderColor = val ? c.border : "#E5E7EB"}
          />
          {val && (
            <button onClick={() => removeCell(idx)} style={{ marginLeft: 2, background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 12, lineHeight: 1, padding: "0 2px" }}>×</button>
          )}
        </div>
      ))}
      <button onClick={() => addCell(cells.length - 1)} style={{ background: "none", border: "1px dashed #D1D5DB", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#9CA3AF", cursor: "pointer" }}>+ add</button>
    </div>
  );
}

/* ─── Excel Bulk Upload Panel ─────────────────────────────────────── */
function ExcelUploadPanel({ onClose, onDone }) {
  const [dragging,  setDragging]  = useState(false);
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result,    setResult]    = useState(null);
  const [error,     setError]     = useState("");
  const inputRef = useRef();

  const accept = (f) => {
    if (!f) return;
    if (!/\.(xlsx|xls|csv)$/i.test(f.name)) { setError("Please upload .xlsx, .xls or .csv"); return; }
    setFile(f); setError(""); setResult(null);
  };

  const downloadTemplate = () => {
    // Build a sample template and download it as blob using SheetJS on frontend
    // We'll create a simple CSV template link instead
    const csv = [
      ["name","indications","dravyas","note"],
      ["Sitopaladi Churna","Vataja Kasa\nKaphaja Kasa\nJwara","Sharkara\nVamshalochana\nPippali\nEla\nTwak","Good for chronic cough. Avoid in Pitta prakriti."],
      ["Trikatu Churna","Agnimandya\nAma\nKaphaja Vyadhi","Shunthi\nMaricha\nPippali","Stimulates digestive fire. Hot potency — use cautiously in summer."],
    ].map(r => r.map(cell => `"${String(cell).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = "medicine_template.csv"; a.click();
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true); setError(""); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/medicines/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(res.data);
      onDone();
    } catch (err) {
      setError(err.response?.data?.message || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 20, padding: 28, width: "100%", maxWidth: 520, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", fontFamily: "'Inter',sans-serif" }}>
        
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#111827", fontFamily: "'Playfair Display',serif" }}>📊 Bulk Upload via Excel</div>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 3 }}>Upload .xlsx / .xls / .csv — one medicine per row</div>
          </div>
          <button onClick={onClose} style={{ background: "#F3F4F6", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 18, color: "#6B7280" }}>×</button>
        </div>

        {/* Column guide */}
        <div style={{ background: "#F8FAFF", border: "1px solid #DBEAFE", borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#1D4ED8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Required Column Names</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px" }}>
            {[
              { col: "name", desc: "Medicine / formulation name" },
              { col: "indications", desc: "Symptoms & diseases (one per line or comma)" },
              { col: "dravyas", desc: "Ingredients / herbs (one per line or comma)" },
              { col: "note", desc: "Warning, tip, or clinical remark" },
            ].map(({ col, desc }) => (
              <div key={col}>
                <code style={{ fontSize: 11, background: "#EFF6FF", color: "#1D4ED8", padding: "1px 6px", borderRadius: 4, fontWeight: 700 }}>{col}</code>
                <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>{desc}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10, fontSize: 10, color: "#94A3B8" }}>
            ✦ Multiple values in one cell: separate by newline (Alt+Enter in Excel), comma, or semicolon<br/>
            ✦ Existing medicines (same name) will be <strong>replaced</strong> — dravyas &amp; indications overwritten with fresh file data<br/>
            ✦ Column names are flexible — "symptoms", "ingredients", "warning" etc. all work
          </div>
        </div>

        {/* Download template */}
        <button onClick={downloadTemplate} style={{ display: "flex", alignItems: "center", gap: 8, background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontSize: 12, color: "#166534", fontWeight: 600, marginBottom: 16 }}>
          ⬇ Download sample template (.csv)
        </button>

        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); accept(e.dataTransfer.files[0]); }}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? "#3B82F6" : file ? "#22C55E" : "#D1D5DB"}`,
            borderRadius: 14, padding: "28px 20px", textAlign: "center", cursor: "pointer",
            background: dragging ? "#EFF6FF" : file ? "#F0FDF4" : "#FAFAFA",
            transition: "all 0.15s", marginBottom: 16,
          }}
        >
          <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={e => accept(e.target.files[0])} />
          <div style={{ fontSize: 28, marginBottom: 8 }}>{file ? "✅" : "📂"}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: file ? "#166534" : "#374151" }}>
            {file ? file.name : "Drag & drop your Excel file here"}
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
            {file ? `${(file.size / 1024).toFixed(1)} KB · click to change` : "or click to browse · .xlsx .xls .csv"}
          </div>
        </div>

        {/* Error */}
        {error && <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 10, padding: "10px 14px", color: "#DC2626", fontSize: 12, marginBottom: 12 }}>{error}</div>}

        {/* Result */}
        {result && (
          <div style={{ background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
            <div style={{ fontWeight: 700, color: "#166534", fontSize: 13, marginBottom: 4 }}>✅ Upload complete</div>
            <div style={{ fontSize: 12, color: "#374151" }}>{result.message}</div>
            {result.errors?.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 11, color: "#DC2626" }}>
                {result.errors.slice(0, 3).map((e, i) => <div key={i}>⚠ {e}</div>)}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, background: "#F3F4F6", border: "none", borderRadius: 10, padding: "10px", fontWeight: 600, fontSize: 13, cursor: "pointer", color: "#374151" }}>Close</button>
          <button
            onClick={upload}
            disabled={!file || uploading}
            style={{ flex: 2, background: file ? "#16A34A" : "#D1D5DB", color: "#fff", border: "none", borderRadius: 10, padding: "10px", fontWeight: 700, fontSize: 13, cursor: file ? "pointer" : "not-allowed", opacity: uploading ? 0.7 : 1 }}
          >
            {uploading ? "Uploading…" : "Upload & Import"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Medicine form (add / edit) ─────────────────────────────────── */
function MedicineForm({ initial, onSave, onCancel, saving }) {
  const [name,        setName]        = useState(initial?.name        || "");
  const [dravyas,     setDravyas]     = useState(initial?.dravyas?.length     ? initial.dravyas     : [""]);
  const [indications, setIndications] = useState(initial?.indications?.length ? initial.indications : [""]);
  const [note,        setNote]        = useState(initial?.note        || "");
  const [error,       setError]       = useState("");

  const submit = () => {
    if (!name.trim()) { setError("Medicine name is required"); return; }
    setError("");
    onSave({ name: name.trim(), dravyas: dravyas.filter(d => d.trim()), indications: indications.filter(i => i.trim()), note: note.trim() });
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, padding: "20px 24px", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", marginBottom: 20 }}>
      
      {/* Name */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>MEDICINE NAME *</label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Sitopaladi Churna, Trikatu Churna"
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 12px", fontSize: 14, fontFamily: "'Inter',sans-serif", outline: "none" }}
          onFocus={e => e.target.style.borderColor = "#3B82F6"} onBlur={e => e.target.style.borderColor = "#D1D5DB"} autoFocus />
        {error && <p style={{ color: "#EF4444", fontSize: 12, margin: "4px 0 0" }}>{error}</p>}
      </div>

      {/* Dravyas */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>DRAVYAS (INGREDIENTS / HERBS) <span style={{ fontWeight: 400, color: "#9CA3AF" }}>— Tab to next cell · paste from Excel</span></label>
        <TagGrid values={dravyas} onChange={setDravyas} placeholder="e.g. Vasa, Pippali" color="green" />
      </div>

      {/* Indications */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>SYMPTOMS & DISEASES (INDICATIONS) <span style={{ fontWeight: 400, color: "#9CA3AF" }}>— one per cell · paste from Excel</span></label>
        <TagGrid values={indications} onChange={setIndications} placeholder="e.g. Vataja Kasa, Jwara" color="blue" />
      </div>

      {/* Note */}
      <div style={{ marginBottom: 20 }}>
        <label style={labelStyle}>CLINICAL NOTE / WARNING / TIP <span style={{ fontWeight: 400, color: "#9CA3AF" }}>— shown to doctors in suggestion panel</span></label>
        <textarea
          value={note} onChange={e => setNote(e.target.value)}
          placeholder="e.g. Contains Pippali — avoid in high Pitta. Ideal post-fever recovery. Allergen: sesame."
          rows={2}
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none", resize: "vertical", color: "#374151" }}
          onFocus={e => e.target.style.borderColor = "#F59E0B"} onBlur={e => e.target.style.borderColor = "#D1D5DB"}
        />
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={submit} disabled={saving} style={{ background: "#16A34A", color: "#fff", border: "none", borderRadius: 10, padding: "9px 22px", fontWeight: 600, fontSize: 13, cursor: saving ? "not-allowed" : "pointer", fontFamily: "'Inter',sans-serif", opacity: saving ? 0.7 : 1 }}>
          {saving ? "Saving…" : "Save Medicine"}
        </button>
        <button onClick={onCancel} style={{ background: "#F3F4F6", color: "#374151", border: "none", borderRadius: 10, padding: "9px 22px", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>Cancel</button>
      </div>
    </div>
  );
}

/* ─── Main page ──────────────────────────────────────────────────── */
export default function AdminMedicines() {
  const [medicines,   setMedicines]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showForm,    setShowForm]    = useState(false);
  const [editMed,     setEditMed]     = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [search,      setSearch]      = useState("");
  const [confirmDel,  setConfirmDel]  = useState(null);
  const [expanded,    setExpanded]    = useState(null);
  const [showUpload,  setShowUpload]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get("/medicines"); setMedicines(res.data || []); }
    catch (err) { console.error("Load medicines:", err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    setSaving(true);
    try {
      editMed ? await api.put(`/medicines/${editMed._id}`, data) : await api.post("/medicines", data);
      await load(); setShowForm(false); setEditMed(null);
    } catch (err) { alert(err.response?.data?.message || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/medicines/${id}`); setMedicines(prev => prev.filter(m => m._id !== id)); setConfirmDel(null); if (editMed?._id === id) { setEditMed(null); setShowForm(false); } }
    catch { alert("Delete failed"); }
  };

  const filtered = medicines.filter(m =>
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.indications.some(i => i.toLowerCase().includes(search.toLowerCase())) ||
    m.dravyas.some(d => d.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div style={{ fontFamily: "'Inter',sans-serif" }}>

      {showUpload && <ExcelUploadPanel onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#1F2937", margin: 0, fontFamily: "'Playfair Display',serif" }}>Medicine Database</h1>
          <p style={{ fontSize: 13, color: "#9CA3AF", margin: "4px 0 0" }}>{medicines.length} medicine{medicines.length !== 1 ? "s" : ""} · Master admin only</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowUpload(true)} style={{ background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE", borderRadius: 10, padding: "9px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            📊 Upload Excel
          </button>
          <button onClick={() => { setEditMed(null); setShowForm(true); }} style={{ background: "#16A34A", color: "#fff", border: "none", borderRadius: 10, padding: "9px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            + Add Medicine
          </button>
        </div>
      </div>

      {/* Add / Edit Form */}
      {(showForm || editMed) && (
        <MedicineForm initial={editMed} onSave={handleSave} onCancel={() => { setShowForm(false); setEditMed(null); }} saving={saving} />
      )}

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, symptom, disease, or dravya…"
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid #E5E7EB", borderRadius: 10, padding: "9px 14px", fontSize: 13, outline: "none" }} />
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 48, color: "#9CA3AF" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>💊</div>
          {search ? "No medicines match your search." : "No medicines yet. Click + Add Medicine or Upload Excel to start."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map(med => (
            <div key={med._id} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
              
              {/* Row header */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", cursor: "pointer" }} onClick={() => setExpanded(expanded === med._id ? null : med._id)}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1F2937" }}>{med.name}</div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
                    {med.dravyas.length} dravya{med.dravyas.length !== 1 ? "s" : ""} · {med.indications.length} indication{med.indications.length !== 1 ? "s" : ""}
                    {med.note && <span style={{ marginLeft: 6, color: "#F59E0B", fontWeight: 600 }}>⚠ note</span>}
                  </div>
                </div>

                {/* Quick indication preview */}
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", maxWidth: 300, justifyContent: "flex-end" }}>
                  {med.indications.slice(0, 3).map((ind, i) => (
                    <span key={i} style={{ background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE", fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20 }}>{ind}</span>
                  ))}
                  {med.indications.length > 3 && <span style={{ fontSize: 10, color: "#9CA3AF" }}>+{med.indications.length - 3} more</span>}
                </div>

                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button onClick={e => { e.stopPropagation(); setEditMed(med); setShowForm(false); window.scrollTo(0, 0); }}
                    style={{ background: "#F3F4F6", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer", color: "#374151", fontWeight: 500 }}>Edit</button>
                  <button onClick={e => { e.stopPropagation(); setConfirmDel(med._id); }}
                    style={{ background: "#FEF2F2", border: "none", borderRadius: 8, padding: "6px 12px", fontSize: 12, cursor: "pointer", color: "#DC2626", fontWeight: 500 }}>Delete</button>
                </div>
                <span style={{ fontSize: 12, color: "#9CA3AF", flexShrink: 0 }}>{expanded === med._id ? "▲" : "▼"}</span>
              </div>

              {/* Expanded detail */}
              {expanded === med._id && (
                <div style={{ padding: "0 18px 16px", borderTop: "1px solid #F3F4F6" }}>
                  {med.dravyas.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={secLabel}>Dravyas</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 5 }}>
                        {med.dravyas.map((d, i) => <span key={i} style={{ background: "#F0FDF4", color: "#166534", border: "1px solid #BBF7D0", fontSize: 11, padding: "3px 10px", borderRadius: 20 }}>{d}</span>)}
                      </div>
                    </div>
                  )}
                  {med.indications.length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={secLabel}>Symptoms & Diseases</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 5 }}>
                        {med.indications.map((ind, i) => <span key={i} style={{ background: "#EFF6FF", color: "#1D4ED8", border: "1px solid #BFDBFE", fontSize: 11, padding: "3px 10px", borderRadius: 20 }}>{ind}</span>)}
                      </div>
                    </div>
                  )}
                  {med.note && (
                    <div style={{ marginTop: 12, background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "10px 14px" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>⚠ Clinical Note</div>
                      <div style={{ fontSize: 12, color: "#78350F", lineHeight: 1.5 }}>{med.note}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Delete confirm */}
              {confirmDel === med._id && (
                <div style={{ padding: "12px 18px", background: "#FFF7F7", borderTop: "1px solid #FEE2E2", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 13, color: "#DC2626", flex: 1 }}>Delete <strong>{med.name}</strong>? This cannot be undone.</span>
                  <button onClick={() => handleDelete(med._id)} style={{ background: "#DC2626", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Yes, delete</button>
                  <button onClick={() => setConfirmDel(null)} style={{ background: "#F3F4F6", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const labelStyle = { fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "'Inter',sans-serif", display: "block", marginBottom: 6 };
const secLabel   = { fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: "0.08em", textTransform: "uppercase" };